import { z } from 'zod';
import { BlockSchema } from './blocks.js';
import { ComponentUsageSchema } from './components.js';

// ─────────────────────────────────────────────────────────────────
// Design tokens
// ─────────────────────────────────────────────────────────────────
export const DesignTokensSchema = z
  .object({
    // Colours
    colorPrimary: z.string().optional(),
    colorText: z.string().optional(),
    colorBackground: z.string().optional(),
    colorSurface: z.string().optional(),
    colorBorder: z.string().optional(),
    // Typography
    fontHeading: z.string().optional(),
    fontBody: z.string().optional(),
    fontMono: z.string().optional(),
    fontSizeBase: z.string().optional(),
    lineHeightBase: z.string().optional(),
    // Layout
    maxWidth: z.string().optional(),
    spacingBase: z.string().optional(),
    radiusBase: z.string().optional(),
    // Header/Footer
    headerBackground: z.string().optional(),
    headerTextColor: z.string().optional(),
    footerBackground: z.string().optional(),
    footerTextColor: z.string().optional(),
  })
  .catchall(z.string()); // allow plugin-defined tokens

export type DesignTokens = z.infer<typeof DesignTokensSchema>;

// ─────────────────────────────────────────────────────────────────
// Post status
// ─────────────────────────────────────────────────────────────────
export const PostStatusSchema = z.enum(['draft', 'review', 'scheduled', 'published', 'archived']);

export type PostStatus = z.infer<typeof PostStatusSchema>;

// ─────────────────────────────────────────────────────────────────
// Post create / update payloads (API request bodies)
// ─────────────────────────────────────────────────────────────────
export const CreatePostSchema = z.object({
  title: z.string().min(1).max(500),
  slug: z
    .string()
    .regex(/^[a-z0-9-]+$/, 'Slug must be lowercase alphanumeric with hyphens')
    .optional(),
});

export const UpdatePostSchema = z.object({
  title: z.string().min(1).max(500).optional(),
  slug: z
    .string()
    .regex(/^[a-z0-9-]+$/)
    .optional(),
  blocks: z.array(BlockSchema).optional(),
  /** Reusable-component usage metadata (see components.ts). */
  componentUsages: z.array(ComponentUsageSchema).optional(),
  excerpt: z.string().max(500).optional(),
  featuredImageId: z.string().optional().nullable(),
  seoTitle: z.string().max(60).optional().nullable(),
  seoDescription: z.string().max(160).optional().nullable(),
  ogTitle: z.string().max(200).optional().nullable(),
  ogDescription: z.string().max(200).optional().nullable(),
  ogImageId: z.string().optional().nullable(),
  canonicalUrl: z.string().url().optional().nullable(),
  focusKeyword: z.string().max(100).optional().nullable(),
  noIndex: z.boolean().optional(),
  schemaType: z.enum(['BlogPosting', 'Article', 'HowTo']).optional(),
  schemaExtra: z.record(z.unknown()).optional().nullable(),
  designOverrides: DesignTokensSchema.optional().nullable(),
  tagIds: z.array(z.string()).optional(),
});

export const PublishPostSchema = z.object({
  scheduledAt: z.string().datetime().optional(), // ISO string for scheduled publishing
});

export type CreatePostInput = z.infer<typeof CreatePostSchema>;
export type UpdatePostInput = z.infer<typeof UpdatePostSchema>;
export type PublishPostInput = z.infer<typeof PublishPostSchema>;

// ─────────────────────────────────────────────────────────────────
// Post response shape (what the API returns)
// ─────────────────────────────────────────────────────────────────
export const PostSummarySchema = z.object({
  id: z.string(),
  title: z.string(),
  slug: z.string(),
  status: PostStatusSchema,
  excerpt: z.string().nullable(),
  featuredImageUrl: z.string().nullable(),
  authorId: z.string(),
  authorName: z.string(),
  publishedAt: z.number().nullable(),
  scheduledAt: z.number().nullable(),
  createdAt: z.number(),
  updatedAt: z.number(),
  readingTimeMinutes: z.number().nullable(),
  wordCount: z.number().nullable(),
  tags: z.array(z.object({ id: z.string(), name: z.string(), slug: z.string() })),
});

export type PostSummary = z.infer<typeof PostSummarySchema>;

// ─────────────────────────────────────────────────────────────────
// Pagination
// ─────────────────────────────────────────────────────────────────
export const PaginationQuerySchema = z.object({
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(20),
  status: PostStatusSchema.optional(),
  tag: z.string().optional(),
  search: z.string().optional(),
  authorId: z.string().optional(),
});

export type PaginationQuery = z.infer<typeof PaginationQuerySchema>;

export type PaginatedResponse<T> = {
  data: T[];
  total: number;
  page: number;
  limit: number;
  totalPages: number;
};
